/**
 * Engagement Manager
 *
 * Pure utility functions for engagement services (Braze + Push Notifications).
 * State lives in EngagementProvider for React reactivity.
 */

import {
  Mutation,
  PlatformEnum,
  UpdatePushTokenInput,
} from '@varo/graphql-schema-types';
import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import DeviceInfo from 'react-native-device-info';
import { logEvent } from 'services/analytics/segment/segment';
import {
  init as initBraze,
  logContentCardClicked as brazeLogContentCardClicked,
  logContentCardDismissed as brazeLogContentCardDismissed,
  logContentCardImpression as brazeLogContentCardImpression,
  logCustomEvent as brazeLogCustomEvent,
  registerPushToken,
  requestContentCardsRefresh as brazeRequestContentCardsRefresh,
  subscribeToContentCardsUpdates as brazeSubscribeToContentCardsUpdates,
  subscribeToInAppMessages,
  updatePushNotificationSubscriptionStatus as brazeUpdatePushNotificationSubscriptionStatus,
  updateUser,
} from 'services/engagement/braze/braze';
import { registerServiceWorker } from 'services/engagement/braze/helpers/registerServiceWorker';
import {
  BrazeContentCard,
  BrazeSuccessProductOfferCard,
  ContentCardsCallback,
} from 'services/engagement/braze/types';
import {
  requestPermissions,
  setupNotificationListeners,
} from 'services/engagement/notifications/notifications';
import { isIOS, isWeb } from 'shared/constants/platform';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { getItem, setItem } from 'shared/utils/asyncStorage';
import { logBreadcrumb, logError } from 'shared/utils/sentry/sentry';

let expoPushToken: string | null = null;
let contentCardsCallback: ContentCardsCallback | null = null;
let hasUpdatedBrazeUser = false;
let hasSubscribedToContentCards = false;
let hasTriggeredListenerRefresh = false;

function subscribeToBrazeContentCardsIfReady(): void {
  if (
    !contentCardsCallback ||
    !hasUpdatedBrazeUser ||
    hasSubscribedToContentCards
  ) {
    return;
  }

  brazeSubscribeToContentCardsUpdates(
    (cards, bottomSheet, productOfferCards, marketingCarouselCards) => {
      const marketingCardsCount = marketingCarouselCards?.length ?? 0;

      if (
        !hasTriggeredListenerRefresh &&
        (cards.length > 0 || marketingCardsCount > 0)
      ) {
        hasTriggeredListenerRefresh = true;
        requestContentCardsRefresh();
      }

      contentCardsCallback?.(
        cards,
        bottomSheet,
        productOfferCards,
        marketingCarouselCards,
      );
    },
  );
  hasSubscribedToContentCards = true;
}

/** Log content card click to Braze */
export function logContentCardClicked(
  card: BrazeContentCard | BrazeSuccessProductOfferCard | BrazeBottomSheetProps,
): void {
  brazeLogContentCardClicked(card);
}

export function logContentCardDismissed(
  card: BrazeContentCard | BrazeBottomSheetProps,
): void {
  brazeLogContentCardDismissed(card);
}

export function logContentCardImpression(
  card: BrazeContentCard | BrazeSuccessProductOfferCard | BrazeBottomSheetProps,
): void {
  brazeLogContentCardImpression(card);
}

export function logCustomEvent(
  eventName: string,
  eventProperties?: Record<string, unknown>,
): void {
  brazeLogCustomEvent(eventName, eventProperties);
}

/**
 * Subscribe to content card updates from Braze
 * Provider calls this and manages state internally
 */
export function subscribeToContentCardsUpdates(
  callback: ContentCardsCallback,
): void {
  contentCardsCallback = callback;
  subscribeToBrazeContentCardsIfReady();
}

export function requestContentCardsRefresh(): void {
  brazeRequestContentCardsRefresh();
}

/**
 * Initialize engagement services (Braze + Push Notifications)
 * Called during app initialization (Phase 5)
 *
 * This handles ONE-TIME initialization:
 * - Braze SDK init
 * - Push notification permissions + token registration
 * - Notification listeners setup
 *
 * Note: Content card subscription is handled by EngagementProvider
 */
export async function initializeEngagement(): Promise<void> {
  try {
    logBreadcrumb({
      level: 'info',
      message: 'Initializing engagement services',
    });

    // Initialize Braze SDK
    initBraze();

    // Register service worker for web push
    if (isWeb) {
      registerServiceWorker();
    }

    // Subscribe to in-app messages (must be ready before user interaction)
    subscribeToInAppMessages();

    // Fetch push token asynchronously
    void requestPermissions()
      .then((token) => {
        if (token) {
          expoPushToken = token;
          registerPushToken(token);

          logBreadcrumb({
            level: 'info',
            message: 'Push notification token received',
          });
        }
      })
      .catch((error) => {
        logBreadcrumb({
          level: 'warning',
          message: 'Failed to fetch push token',
          data: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      });

    // Set up notification listeners (for deeplink handling from push)
    setupNotificationListeners(logEvent);

    logBreadcrumb({
      level: 'info',
      message: 'Notification listeners configured',
    });

    logBreadcrumb({
      level: 'info',
      message: 'Engagement services initialized successfully',
    });
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to initialize engagement services',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
  }
}

/**
 * Update Braze user after login
 * Called from userInfoManager.syncUserIdentityToServices()
 */
export function updateEngagementUser(identityId: string): void {
  updateUser(identityId);
  hasUpdatedBrazeUser = true;
  hasTriggeredListenerRefresh = false;
  subscribeToBrazeContentCardsIfReady();

  logBreadcrumb({
    level: 'info',
    message: 'Braze user updated',
    data: { identityId },
  });
}

export function updatePushNotificationSubscriptionStatus(
  isEnabled: boolean,
): void {
  brazeUpdatePushNotificationSubscriptionStatus(isEnabled);
}

/**
 * Get the expo push token (stored during initialization)
 */
export function getExpoPushToken(): string | null {
  return expoPushToken;
}

/**
 * Update push token in backend
 * Called after login when we have both token and user session
 */
export async function syncPushTokenWithBackend(
  updatePushTokenMutation: (variables: {
    idempotencyId: string;
    input: UpdatePushTokenInput;
  }) => Promise<{ data?: Mutation }>,
  idempotencyToken: string,
): Promise<void> {
  if (!expoPushToken) {
    return;
  }

  try {
    const deviceId = await getItem(STORAGE_KEYS.DEVICE_INFO_ID);

    const input: UpdatePushTokenInput = {
      deviceToken: expoPushToken,
      platform: isIOS ? PlatformEnum.Ios : PlatformEnum.Android,
      osName: DeviceInfo.getSystemName(),
      osVersion: DeviceInfo.getSystemVersion(),
      appVersion: DeviceInfo.getVersion(),
      id: deviceId ?? '',
    };

    const result = await updatePushTokenMutation({
      idempotencyId: idempotencyToken,
      input,
    });

    // Store device info ID for future use
    const updateResult = result?.data?.updatePushToken;
    if (
      updateResult &&
      'id' in updateResult &&
      typeof updateResult.id === 'string'
    ) {
      await setItem(STORAGE_KEYS.DEVICE_INFO_ID, updateResult.id);

      logBreadcrumb({
        level: 'info',
        message: 'Push token synced with backend',
      });
    }
  } catch (error) {
    logError(error);
    logBreadcrumb({
      level: 'error',
      message: 'Failed to sync push token with backend',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
    // Don't throw - this shouldn't break the app
  }
}
